import { IAssociatedContact } from '@app/models/AssociatedContact'
import { IBrokeragePipeline } from '@app/models/BrokeragePipeline'
import { IBrokeragePipelineCommissionFee } from '@app/models/BrokeragePipelineCommissionFee'
import { ISiteSettings } from '@app/models/SiteSettings'
import { ICurrentUserState } from '@app/store/modules/account'
import { formatCurrency, formatNumber } from '@app/utils/formatting'
import classNames from 'classnames'
import * as React from 'react'
import { Link } from 'react-router-dom'
import './FeeBonusDistribution.scss'

interface IFeeBonusDistributionProps {
  pipeline: IBrokeragePipeline
  siteSettings: ISiteSettings
}

type FeeBonusDistributionProps = IFeeBonusDistributionProps & ICurrentUserState

export default class FeeBonusDistribution extends React.PureComponent<
  FeeBonusDistributionProps
> {
  public render() {
    const listingFee = this.props.pipeline.listingFee
    const sellingFee = this.props.pipeline.sellingFee
    const totalFeePercentage =
      listingFee.totalFeePercent + sellingFee.totalFeePercent
    const totalFeeAmount = this._getFeeSplit(
      this.props.pipeline.salesPrice,
      totalFeePercentage,
    )

    return (
      <div>
        <h6 className='text-center'>
          Total Fees {formatCurrency(totalFeeAmount, 2)} (
          {formatNumber(totalFeePercentage, 2)}%)
        </h6>
        <div className='fee-distribution'>
          {this._renderDetail('Listing', listingFee, 'left')}
          {this._renderDetail('Selling', sellingFee, 'right')}
        </div>
      </div>
    )
  }

  private _canEditFeeDistribution() {
    return (
      this.props.user &&
      (this.props.user.permissions.isAdmin ||
        this.props.user.permissions.editFeeDistribution === true)
    )
  }

  private _getFeeSplit(total: number, split: number) {
    return (total * split) / 100
  }

  private _renderDetail(
    title: string,
    fees: IBrokeragePipelineCommissionFee,
    className: string,
  ) {
    const totalAmount = this._getFeeSplit(
      this.props.pipeline.salesPrice,
      fees.totalFeePercent,
    )

    return (
      <div className={classNames('fee-distribution-group', className)}>
        <div className='text-center font-weight-bold'>{title}</div>
        <div className='text-center'>
          {formatCurrency(totalAmount, 2)} (
          {formatNumber(fees.totalFeePercent, 2)}%)
        </div>
        {this._renderFeeTo(fees, totalAmount)}
        {this._renderBonuses(fees, totalAmount)}
      </div>
    )
  }

  private _renderFeeTo(fees: IBrokeragePipelineCommissionFee, total: number) {
    if (!this._canEditFeeDistribution() && fees.individualSplits.size === 0) {
      return null
    }

    return (
      <div className='my-2'>
        <div className='font-weight-bold'>Fee to</div>
        {this._canEditFeeDistribution() &&
          this._renderIndividualSplit(
            this.props.siteSettings.brokerageCompany,
            fees.companySplit,
            total,
          )}
        {fees.individualSplits.toArray().map((ind, i) => {
          return this._renderIndividualSplit(
            ind.contact,
            ind.amount,
            total,
            `indSplit-${i}`,
          )
        })}
      </div>
    )
  }

  private _renderBonuses(fees: IBrokeragePipelineCommissionFee, total: number) {
    if (fees.bonuses.size === 0) {
      return null
    }

    return (
      <div className='my-2'>
        <div className='font-weight-bold'>Bonus</div>
        {fees.bonuses.toArray().map((ind, i) => (
          <div key={`bonus-${i}`} className='d-flex'>
            <div className='w-50'>
              {ind.contact !== undefined && (
                <Link
                  to={`/connection/${ind.contact.contactId}`}
                  target='_blank'
                >
                  {ind.contact.name}
                </Link>
              )}
            </div>
            <div className='w-50 text-right'>
              {formatCurrency(ind.amount, 2)}
            </div>
          </div>
        ))}
      </div>
    )
  }

  private _renderIndividualSplit(
    ind: IAssociatedContact | undefined,
    split: number,
    total: number,
    key?: string,
  ) {
    return (
      <div className='d-flex' key={key}>
        <div className='w-50'>
          {ind !== undefined && (
            <Link to={`/connection/${ind.contactId}`} target='_blank'>
              {ind.name}
            </Link>
          )}
        </div>
        <div className='w-50 text-right'>
          {formatCurrency(this._getFeeSplit(total, split), 2)}
        </div>
      </div>
    )
  }
}
